import { useEffect, useState, useRef } from "react";
import { Pause, Play } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { SUPABASE_URL } from "@/integrations/supabase/env";
import { PodcasterCard } from "./PodcasterCard";
import { AnimateOnScroll } from "@/components/ui/animate-on-scroll";
import { Button } from "@/components/ui/button";
import { useMarquee, MARQUEE_SPEED_ROW1, MARQUEE_SPEED_ROW2 } from "@/hooks/useMarquee";

// ============================================================================
// TYPES
// ============================================================================

export interface FeaturedShow {
  id: string;
  showName: string;
  episodeName: string;
  artworkGradient: string;
  allTimeStreams: number;
  coverUrl: string | null;
  slug: string;
}

// ============================================================================
// MOCK FALLBACK DATA (DEV ONLY)
// ============================================================================

const mockFeaturedShows: FeaturedShow[] = [
  { id: "1", showName: "The Daily Grind", episodeName: "Ep. 247: Morning Routines", artworkGradient: "bg-gradient-to-br from-accent to-accent/60", allTimeStreams: 0, coverUrl: null, slug: "" },
  { id: "2", showName: "True Crime Weekly", episodeName: "The Vanishing Act", artworkGradient: "bg-gradient-to-br from-destructive to-destructive/60", allTimeStreams: 0, coverUrl: null, slug: "" },
  { id: "3", showName: "Tech Forward", episodeName: "AI Revolution Part 3", artworkGradient: "bg-gradient-to-br from-accent-gold to-accent-gold/60", allTimeStreams: 0, coverUrl: null, slug: "" },
  { id: "4", showName: "Mindful Minutes", episodeName: "Finding Peace Within", artworkGradient: "bg-gradient-to-br from-success to-success/60", allTimeStreams: 0, coverUrl: null, slug: "" },
  { id: "5", showName: "Sports Talk Live", episodeName: "Playoff Predictions", artworkGradient: "bg-gradient-to-br from-warning to-warning/60", allTimeStreams: 0, coverUrl: null, slug: "" },
  { id: "6", showName: "Comedy Hour", episodeName: "Best of 2025", artworkGradient: "bg-gradient-to-br from-accent to-accent-gold", allTimeStreams: 0, coverUrl: null, slug: "" },
  { id: "7", showName: "History Uncovered", episodeName: "Ancient Rome Secrets", artworkGradient: "bg-gradient-to-br from-neutral to-neutral/60", allTimeStreams: 0, coverUrl: null, slug: "" },
  { id: "8", showName: "Startup Stories", episodeName: "From Zero to IPO", artworkGradient: "bg-gradient-to-br from-accent to-success", allTimeStreams: 0, coverUrl: null, slug: "" },
  { id: "9", showName: "Night Owl Radio", episodeName: "Late Night Confessions", artworkGradient: "bg-gradient-to-br from-primary to-accent", allTimeStreams: 0, coverUrl: null, slug: "" },
  { id: "10", showName: "Fitness Focus", episodeName: "30-Day Challenge Results", artworkGradient: "bg-gradient-to-br from-success to-accent-gold", allTimeStreams: 0, coverUrl: null, slug: "" },
  { id: "11", showName: "Book Club Live", episodeName: "This Month's Pick", artworkGradient: "bg-gradient-to-br from-accent-gold to-warning", allTimeStreams: 0, coverUrl: null, slug: "" },
  { id: "12", showName: "Travel Tales", episodeName: "Hidden Gems of Japan", artworkGradient: "bg-gradient-to-br from-accent to-destructive/70", allTimeStreams: 0, coverUrl: null, slug: "" },
];

// Deterministic gradient from show id
const GRADIENTS = [
  "bg-gradient-to-br from-accent to-accent/60",
  "bg-gradient-to-br from-destructive to-destructive/60",
  "bg-gradient-to-br from-accent-gold to-accent-gold/60",
  "bg-gradient-to-br from-success to-success/60",
  "bg-gradient-to-br from-accent to-accent-gold",
  "bg-gradient-to-br from-neutral to-neutral/60",
  "bg-gradient-to-br from-accent to-success",
  "bg-gradient-to-br from-primary to-accent",
];

function gradientForId(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return GRADIENTS[Math.abs(hash) % GRADIENTS.length];
}

// ============================================================================
// STABLE DISPLAY ORDER
// ============================================================================

/** Seeded hash for stable shuffle (same input → same output every render) */
function seededShuffle(arr: FeaturedShow[]): FeaturedShow[] {
  const copy = [...arr];
  let seed = 0;
  for (const item of copy) {
    for (let i = 0; i < item.id.length; i++) {
      seed = (seed * 31 + item.id.charCodeAt(i)) | 0;
    }
  }
  const rng = () => {
    seed = (seed * 1664525 + 1013904223) | 0;
    return (seed >>> 0) / 4294967296;
  };
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Split into two disjoint row datasets so the rows never show identical content. */
function splitForRows(deduped: FeaturedShow[]): { row1Cards: FeaturedShow[]; row2Cards: FeaturedShow[] } {
  if (deduped.length === 0) return { row1Cards: [], row2Cards: [] };

  const shuffled = seededShuffle(deduped);

  if (deduped.length >= 8) {
    const row1: FeaturedShow[] = [];
    const row2: FeaturedShow[] = [];
    shuffled.forEach((show, i) => (i % 2 === 0 ? row1 : row2).push(show));
    return { row1Cards: row1, row2Cards: row2 };
  }

  // Small sets: reuse all cards in both rows but offset row 2 so they differ visually
  const mid = Math.ceil(shuffled.length / 2);
  return {
    row1Cards: shuffled,
    row2Cards: [...shuffled.slice(mid), ...shuffled.slice(0, mid)],
  };
}

/** Upper bound on cards per marquee row — see the split usage below. */
const MAX_CARDS_PER_ROW = 10;

// ============================================================================
// DATA FETCH HELPERS
// ============================================================================

function toFeaturedShows(data: unknown): FeaturedShow[] {
  if (!Array.isArray(data)) {
    throw new Error("Unexpected response shape");
  }

  return data
    .filter((show): show is Record<string, unknown> => !!show && typeof show === "object")
    .map((show) => {
      const id = String(show.id ?? "");
      if (!id) return null;

      const allTimeStreamsValue = Number(show.allTimeStreams ?? 0);

      return {
        id,
        showName: String(show.showName ?? "Untitled Show"),
        episodeName: String(show.episodeName ?? "New Show"),
        artworkGradient: gradientForId(id),
        allTimeStreams: Number.isFinite(allTimeStreamsValue) ? allTimeStreamsValue : 0,
        coverUrl: typeof show.coverUrl === "string" && show.coverUrl.length > 0 ? show.coverUrl : null,
        slug: typeof show.slug === "string" ? show.slug : "",
      } satisfies FeaturedShow;
    })
    .filter((show): show is FeaturedShow => show !== null);
}

const FEATURED_SHOWS_TIMEOUT_MS = 8_000;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => reject(new Error(`${label} timed out`)), timeoutMs);

    promise
      .then((value) => {
        clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });
}

async function fetchFeaturedShowsDirect(): Promise<FeaturedShow[]> {
  const url = `${SUPABASE_URL}/functions/v1/homepage-featured-shows`;
  const res = await withTimeout(fetch(url, { method: "GET" }), FEATURED_SHOWS_TIMEOUT_MS, "Direct featured shows fetch");

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }

  const raw = await withTimeout(res.text(), FEATURED_SHOWS_TIMEOUT_MS, "Direct featured shows response parse");
  const parsed = raw.length ? JSON.parse(raw) : [];

  return toFeaturedShows(parsed);
}

async function fetchFeaturedShowsViaClient(): Promise<FeaturedShow[]> {
  const { data, error } = await withTimeout(
    supabase.functions.invoke("homepage-featured-shows", {
      method: "GET",
    }),
    FEATURED_SHOWS_TIMEOUT_MS,
    "Client invoke featured shows",
  );

  if (error) {
    throw error;
  }

  return toFeaturedShows(data);
}

/** Direct fetch with client-invoke fallback; shared with the hero avatars. */
export async function fetchFeaturedShows(): Promise<FeaturedShow[]> {
  try {
    return await fetchFeaturedShowsDirect();
  } catch (directError) {
    console.warn("[FeaturedPodcasters] direct fetch failed, retrying via client invoke:", directError);
    return fetchFeaturedShowsViaClient();
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

export const FeaturedPodcastersSection = () => {
  const [fetchedShows, setFetchedShows] = useState<FeaturedShow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  // Motion control: hover pauses both rows; the toggle hard-pauses until pressed again
  const [hovered, setHovered] = useState(false);
  const [hardPaused, setHardPaused] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const motionPaused = hardPaused || hovered;

  // Defer fetch until section is near viewport
  const sectionRef = useRef<HTMLElement>(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasFetched.current) {
          hasFetched.current = true;
          observer.disconnect();
          void doFetch();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Safety: if observer fails, fetch after 3s
    const timer = setTimeout(() => {
      if (!hasFetched.current) {
        hasFetched.current = true;
        void doFetch();
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  const doFetch = async () => {
    setIsLoading(true);
    setIsError(false);

    try {
      let shows: FeaturedShow[];

      try {
        shows = await fetchFeaturedShowsDirect();
      } catch (directError) {
        console.warn("[FeaturedPodcasters] direct fetch failed, retrying via client invoke:", directError);
        shows = await fetchFeaturedShowsViaClient();
      }

      setFetchedShows(shows);
    } catch (error) {
      console.error("[FeaturedPodcasters] failed to load featured shows:", error);
      setFetchedShows([]);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Use fetched data; fall back to mock ONLY in dev
  const isDev = import.meta.env.DEV;
  const hasRealData = fetchedShows && fetchedShows.length > 0;
  const sourceShows = hasRealData ? fetchedShows : isDev ? mockFeaturedShows : [];

  const deduped = sourceShows.filter((show, index, arr) => arr.findIndex((s) => s.showName === show.showName) === index);
  const split = splitForRows(deduped);
  // Each row is rendered twice (visible + clone) for the seamless loop, so an
  // uncapped list multiplies both cover downloads and DOM nodes on the homepage.
  const row1Cards = split.row1Cards.slice(0, MAX_CARDS_PER_ROW);
  const row2Cards = split.row2Cards.slice(0, MAX_CARDS_PER_ROW);

  // Determine what to render in the marquee area
  const hasCards = row1Cards.length > 0 || row2Cards.length > 0;

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-primary overflow-hidden">
      <div className="container mx-auto px-4 mb-12">
        <AnimateOnScroll animation="fade-up" triggerOnce>
          <h2 className="type-h2 text-center text-primary-foreground">
            Podcasters See{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage: "linear-gradient(to right, hsl(var(--accent)) 0%, hsl(var(--accent-gold)) 100%)",
              }}
            >
              Results
            </span>
          </h2>
        </AnimateOnScroll>
        <AnimateOnScroll animation="fade-up" delay={150} triggerOnce>
          <p className="type-lead text-primary-foreground/80 text-center mt-2">
            With PODGO — You Will, Too.
          </p>
        </AnimateOnScroll>
      </div>

      {/* Featured podcaster cards */}
      {isLoading ? (
        <div className="flex justify-center py-12" role="presentation">
          <div className="flex gap-4 sm:gap-5 md:gap-6 overflow-hidden px-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex-shrink-0 w-[200px] sm:w-[240px] md:w-[280px]">
                <div className="bg-neutral/10 border border-neutral/20 rounded-xl overflow-hidden animate-pulse">
                  <div className="aspect-square bg-neutral/20" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-neutral/20 rounded w-3/4" />
                    <div className="h-3 bg-neutral/20 rounded w-full" />
                    <div className="h-8 bg-neutral/20 rounded w-full mt-2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      ) : hasCards ? (
        <div className="relative">
          {/* Left Fade */}
          <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 md:w-32 bg-gradient-to-r from-primary via-primary/80 to-transparent z-10 pointer-events-none" />
          {/* Right Fade */}
          <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 md:w-32 bg-gradient-to-l from-primary via-primary/80 to-transparent z-10 pointer-events-none" />

          {/* Pause / play control (WCAG 2.2.2 — mechanism to stop moving content).
              Sits outside the hover-pause region so pressing Play resumes immediately. */}
          {!reducedMotion && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setHardPaused((p) => !p)}
              aria-pressed={hardPaused}
              aria-label={hardPaused ? "Resume scrolling carousel" : "Pause scrolling carousel"}
              className="absolute right-3 -top-9 sm:right-4 z-20 h-11 w-11 sm:h-9 sm:w-9 rounded-full border-primary-foreground/25 bg-primary/70 text-primary-foreground backdrop-blur hover:bg-primary-foreground/10 hover:text-primary-foreground"
            >
              {hardPaused ? <Play className="h-4 w-4" aria-hidden="true" /> : <Pause className="h-4 w-4" aria-hidden="true" />}
            </Button>
          )}

          {/* Hover / keyboard focus anywhere in the rows pauses both rows */}
          <div
            className="space-y-4 sm:space-y-5 md:space-y-6"
            onPointerEnter={() => setHovered(true)}
            onPointerLeave={() => setHovered(false)}
            onFocusCapture={() => setHovered(true)}
            onBlurCapture={() => setHovered(false)}
          >
            <MarqueeRow cards={row1Cards} speed={MARQUEE_SPEED_ROW1} paused={motionPaused} />
            <MarqueeRow cards={row2Cards} speed={-MARQUEE_SPEED_ROW2} paused={motionPaused} />
          </div>
        </div>
      ) : isError ? (
        <p className="text-center text-primary-foreground/50 py-8 text-sm">
          Unable to load featured podcasters. Please try again later.
        </p>
      ) : null}
    </section>
  );
};

// ============================================================================
// MARQUEE ROW SUB-COMPONENT
// ============================================================================

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);

  return reduced;
}

function MarqueeRow({ cards, speed, paused }: { cards: FeaturedShow[]; speed: number; paused: boolean }) {
  const reducedMotion = usePrefersReducedMotion();
  // Keyboard focus inside the row freezes motion, exactly like hover does, so
  // a focused card cannot slide out from under the user.
  const [focusWithin, setFocusWithin] = useState(false);
  const { containerRef, trackRef, handlers, ready, revealElement } = useMarquee({
    speed,
    enabled: cards.length > 0 && !reducedMotion,
    cardCount: cards.length,
    paused: paused || focusWithin,
  });

  // Reduced motion (or not yet measured) → plain horizontally scrollable row
  const animated = ready && !reducedMotion;

  const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    setFocusWithin(true);
    if (animated) revealElement(event.target as HTMLElement);
  };

  return (
    <div
      ref={containerRef}
      className={
        animated
          ? "overflow-hidden cursor-grab active:cursor-grabbing select-none"
          : "overflow-x-auto select-none"
      }
      style={{ touchAction: "pan-y" }}
      onFocus={handleFocus}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setFocusWithin(false);
        }
      }}
    >
      <div
        ref={trackRef}
        className={
          animated
            ? "flex w-max gap-4 sm:gap-5 md:gap-6 will-change-transform"
            : "flex gap-4 sm:gap-5 md:gap-6 w-max px-4"
        }
        {...(animated ? handlers : {})}
      >
        {cards.map((show, index) => (
          <div key={`${show.id}-${index}`} className="w-[200px] shrink-0 sm:w-[240px] md:w-[280px]">
            <PodcasterCard show={show} />
          </div>
        ))}
        {/* Visual clone set: useMarquee needs a 2x wide track for a seamless
            loop. It is the same content as above, so it is removed from both
            the a11y tree and the tab order -- otherwise keyboard and screen
            reader users hit every show twice. `inert` (not just aria-hidden)
            is what makes the clones unfocusable, which also keeps axe's
            aria-hidden-focus rule satisfied. */}
        {!reducedMotion && (
          <div
            className="flex shrink-0 gap-4 sm:gap-5 md:gap-6"
            data-marquee-clone=""
            aria-hidden="true"

            // React 18 does not type `inert`; it must be a string attribute.
            {...({ inert: "" } as Record<string, string>)}
          >
            {cards.map((show, index) => (
              <div key={`${show.id}-clone-${index}`} className="w-[200px] shrink-0 sm:w-[240px] md:w-[280px]">
                <PodcasterCard show={show} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

